import { Moment } from "moment-timezone";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  autoUpdate,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { BookingConfig, BookingOption } from "@ct-react/calendar";
import { actions, booking } from "../../../../i18n/sharable-defs";
import { BookingInputHandler, BookingInputProps } from "./index";
import Button from "../../../common/button";
import PeriodResumeInput from "../../../inputs/readonly/period";
import WrapBookingCalendar, { WrapBookingCalendarHandler } from "../../../common/wrap-booking-calendar";
import "./index.scss";

type Props = BookingInputProps<BookingOption<Moment>> & {
  config?: BookingConfig<Moment>;
}

const PeriodInput = forwardRef<BookingInputHandler, Props>(({ sticked, config, value }, forwardedRef) => {

  const intl = useIntl();

  const calendarRef = useRef<WrapBookingCalendarHandler>(null);
  const stickedCalendarRef = useRef<WrapBookingCalendarHandler>(null);

  const [ resettable, setResettable ] = useState<boolean>(!!value);
  const [ pickerOpen, setPickerOpen ] = useState<boolean>(false);

  const { refs, context, floatingStyles } = useFloating({
    open: pickerOpen,
    onOpenChange: setPickerOpen,
    strategy: "absolute",
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(({ rects, elements }) => {
        elements.floating.style.setProperty("--ccv-article-aside-reference-width", `${rects.reference.width}px`);
        return -1 * rects.reference.height;
      })
    ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { toggle: false }),
    useDismiss(context, { outsidePress: true })
  ]);

  useImperativeHandle(forwardedRef, () => ({
    open: () => setPickerOpen(true)
  }));

  const onReset = () => {
    calendarRef.current?.reset();
    stickedCalendarRef.current?.reset();
  };

  if (sticked)
    return (<WrapBookingCalendar ref={stickedCalendarRef}
                                 config={config}
                                 numberOfMonths={1}
                                 resettableChange={setResettable} />);

  return (
    <>
      <PeriodResumeInput ref={refs.setReference}
                         {...getReferenceProps()}
                         value={value?.period} />
      {pickerOpen &&
        <FloatingPortal>
          <div ref={refs.setFloating}
               {...getFloatingProps()}
               className="vars-for-portal picker-wrap period"
               style={floatingStyles}>
            <div className="picker-header">
              <PeriodResumeInput className="faking-ref" value={value?.period} />
            </div>
            <WrapBookingCalendar ref={calendarRef}
                                 config={config}
                                 numberOfMonths={2}
                                 resettableChange={setResettable} />
            <div className="picker-footer">
              <Button type="button" className="link small no-border"
                      disabled={!resettable}
                      onClick={onReset}>
                {intl.formatMessage(booking.reset)}
              </Button>
              <Button type="button" onClick={() => setPickerOpen(false)}>
                {intl.formatMessage(!!value ? actions.validate : actions.close)}
              </Button>
            </div>
          </div>
        </FloatingPortal>
      }
    </>);

});

export default PeriodInput;
